import React from "react";
import { Link } from "react-router-dom";
import { ImageOff, Star, Images } from "lucide-react";
import { CAT_TONE, SOURCE_TONE, checklistMeta, photoSrc, price } from "@/lib/catalogue";

export const Pill = ({ tone, children, testid }) => <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold whitespace-nowrap ${tone}`}>{children}</span>;
export const SourcePill = ({ s }) => <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${SOURCE_TONE[s.source_type] || SOURCE_TONE.other}`}>{s.source_type_label}</span>;

export function Photo({ item, className = "", testid }) {
  const src = photoSrc(item);
  return src
    ? <img src={src} alt={item.item_name} loading="lazy" data-testid={testid} className={`object-cover bg-slate-950 ${className}`} />
    : <div data-testid={testid} className={`grid place-items-center bg-slate-950/60 text-slate-700 ${className}`}><ImageOff className="w-7 h-7" /></div>;
}

export function StatusBadge({ item }) {
  const m = checklistMeta(item.checklist_status);
  return (
    <span className="flex items-center gap-1">
      {item.favourite && <Star className="w-3.5 h-3.5 fill-amber-300 text-amber-300" data-testid="card-fav" />}
      {m && <Pill tone={m[3]} testid="card-status">{m[2]} {m[1]}</Pill>}
    </span>
  );
}

// Catalogue card: photo → name → vehicle → price → preferred source
export default function ItemCard({ item }) {
  const p = item.preferred_source;
  const shown = item.price != null ? price(item.price, item.price_currency, item.price_unit) : p?.price != null ? price(p.price, p.currency, p.price_unit) : "";
  return (
    <Link to={`/inventory/items/${item.id}`} data-testid={`item-card-${item.id}`} className="group rounded-xl border border-white/[0.06] bg-[#151D2E] overflow-hidden flex flex-col hover:border-violet-400/50 hover:-translate-y-0.5 transition-all min-w-0">
      <div className="relative aspect-[4/3]">
        <Photo item={item} className="w-full h-full" />
        <div className="absolute top-2 left-2 flex gap-1"><Pill tone={CAT_TONE[item.category]}>{item.category_label}</Pill></div>
        <div className="absolute top-2 right-2"><StatusBadge item={item} /></div>
        {item.photo_count > 1 && <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded bg-black/60 border border-white/10 px-1.5 py-0.5 text-[10px] font-mono-num text-slate-200" data-testid="card-photo-count"><Images className="w-3 h-3" /> {item.photo_count}</span>}
      </div>
      <div className="p-3 space-y-1 min-w-0">
        <div className="font-semibold text-sm text-slate-50 leading-snug line-clamp-2" data-testid="card-name">{item.item_name}</div>
        <div className="text-[11px] text-slate-400 truncate">{[item.subcategory, item.vehicle, item.brand].filter(Boolean).join(" · ") || "—"}</div>
        <div className="flex items-center justify-between gap-2 pt-1">
          <span className="font-mono-num font-bold text-violet-200" data-testid="card-price">{shown || <span className="text-slate-600 font-normal">No price</span>}</span>
          {p ? <span className="flex items-center gap-1.5 min-w-0"><SourcePill s={p} /><span className="text-[11px] text-slate-400 truncate max-w-[110px]">{p.supplier_name}</span></span> : <span className="text-[11px] text-slate-600">No source</span>}
        </div>
        {item.source_count > 1 && <div className="text-[10px] text-slate-500">+{item.source_count - 1} more source{item.source_count === 2 ? "" : "s"}</div>}
      </div>
    </Link>
  );
}
